<template>
  <div v-if="contentLoaded"
       class="columns is-marginless info-container">
    <content-preview class="figure"
                     :content="content" />
    <div class="column info-content-container"
          :class="{ 'part-collection-info-content-container': !!$slots.partCollections }">
      <article>
        <div class="media-content">
          <div class="content">
            <p>
              <strong data-testid="name">{{ content.name }}</strong>
              <br v-if="identifier">
              <span v-if="showPartNumber"
                    class="has-text-weight-semibold is-flex is-align-items-center part-number-row"
                    data-testid="identifier">
                <span>{{ $t('partNumber') }}: {{ content.partNumber }}</span>
                <button v-if="!isWidget" class="button is-white is-small ml-2 copy-part-number-btn"
                        data-testid="copy-part-number"
                        @click="copyPartNumber">
                  <span class="icon far fa-copy fa-lg copy-part-number-icon" />
                </button>
              </span>
              <span v-else-if="identifier"
                    class="has-text-weight-semibold"
                    data-testid="identifier">
                {{ identifier }}
              </span>
            </p>
            <p v-if="content.description">
              {{ content.description }}
            </p>
            <p v-if="showSupplierName">
              {{ $t('supplier') }}: {{ content.supplierName }}
            </p>
          </div>
        </div>
      </article>
      <slot name="content">
        <template v-if="loading">
          <utility-block />
        </template>
        <template v-else>
          <info-add-to-cart v-if="showInfoAddToCartBlock"
                            :add-to-cart-qty-behavior="addToCartQtyBehavior"
                            :content="content"
                            :display-availability="displayAvailability"
                            :display-discounted="displayDiscounted"
                            :display-eta="displayEta"
                            :display-retail="displayRetail"
                            :display-whole-sale="displayWholeSale"
                            :hasWidgetCart="isWidgetCartEnabled"
                            :is-cart-or-erp-cart-enabled="showAddToCartButtons"
                            :pricing="pricing || {}"
                            :show-pricing-for-parts="showPricingForParts"
                            @widgetAddToCart="handleWidgetAddToCart" />
          <template v-if="showTags">
            <hr>
            <info-tags :id="content.id"
                       :type="content.type"/>
          </template>
          <slot name="partCollections"/>
        </template>
      </slot>
    </div>
  </div>
</template>

<script>
import ContentPreview from '@/components/library/content/info/ContentPreview.vue'
import InfoAddToCart from './InfoAddToCart'
import InfoTags from './tags/InfoTags'
import { mapGetters } from 'vuex'
import { useWidgetAddToCart } from '@/components/widgets/composables/useWidgetAddToCart'
import { UtilityBlock, Thumbnail } from '@/components/common'

export default {
  name: 'Info',
  props: {
    addToCartQtyBehavior: {
      type: String,
      default: ''
    },
    content: {
      type: Object,
      required: true
    },
    contentLoaded: {
      type: Boolean,
      required: true
    },
    displayAvailability: {
      type: Boolean
    },
    displayDiscounted: {
      type: Boolean
    },
    displayEta: {
      type: Boolean
    },
    displayRetail: {
      type: Boolean
    },
    displayWholeSale: {
      type: Boolean
    },
    isCartEnabled: {
      type: Boolean
    },
    isCartOrErpCartEnabled: {
      type: Boolean
    },
    pricing: {
      type: Object,
      default: () => {}
    },
    showTags: {
      type: Boolean,
      default: true
    },
    displaySupplierPartInfo: Boolean,
    showPricingForParts: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { handleWidgetAddToCart } = useWidgetAddToCart()

    return { handleWidgetAddToCart }
  },
  components: {
    ContentPreview,
    InfoAddToCart,
    InfoTags,
    Thumbnail,
    UtilityBlock
  },
  watch: {
    contentLoaded () {
      this.$emit('getPricing')
    }
  },
  computed: {
    ...mapGetters({
      isWidget: 'widgets/isWidget',
      isWidgetCartEnabled: 'widgets/isWidgetCartEnabled',
    }),
    showSupplierName () {
      return this.displaySupplierPartInfo
        && this.content.type === 'part'
        && this.content.supplierName
    },
    createdDate () {
      return this.content.created ? this.$i18n.d(this.content.created, 'short') : ''
    },
    entityType () {
      return (this.content.contentType || this.content.type ||
              this.content.mediaType || this.content.entityType)
    },
    identifier () {
      if (this.content.partNumber) {
        return `${this.$i18n.t('partNumber')}: ${this.content.partNumber}`
      }

      if (this.content.type === "product-page") {
        return `${this.$i18n.t('productId')}: ${this.content.productId}`
      }

      if (this.content.identifier) {
        switch (this.entityType) {
          case 'Part':
            return `${this.$i18n.t('partNumber')}: ${this.content.identifier}`
          default:
            if (!this.$store.state.user.hideIdentifier) {
              return `${this.$i18n.t('identifier')}: ${this.content.identifier}`
            }
        }
      }

      return ''
    },
    showPartNumber () {
      return !!this.content?.partNumber
    },
    loading () {
      return (this.$store.getters['widgets/isWidget'])
        ? false
        : (!this.$store.state.cart.isLoaded && this.isCartEnabled)
          || !this.pricing.isLoaded

    },
    showAddToCartButtons() {
      return !(this.content.partNumber && this.content.orderable) ? false
        : this.isWidget ? this.isWidgetCartEnabled
        : this.isCartOrErpCartEnabled
    },
    showInfoAddToCartBlock() {
      const isPart = this.showPricingForParts && this.entityType?.toLowerCase() === 'part'
      const hasContent = isPart || (this.content.orderable && !!this.content.partNumber)
      const pricingReady = this.pricing.isLoaded || (this.isWidget && this.isWidgetCartEnabled)
      return hasContent && pricingReady
    },
    updatedDate () {
      return this.content.updated ? this.$i18n.d(this.content.updated, 'short') : ''
    }
  },
  methods: {
    copyPartNumber () {
      this.copyToClipboard(this.content.partNumber)
    },
    copyToClipboard (value) {
      navigator.clipboard.writeText(value)
      this.$notify({
        type: 'success',
        title: this.$t('success'),
        text: this.$t('partNumberCopied', { label: this.$t('partNumber') })
      })
    },
    addToCart (partWithQuantityObject) {
      this.$emit('addToCart', partWithQuantityObject)
    }
  }
}
</script>

<style scoped>
.info-container {
  display: flex;
    @media screen and (max-width: 1023px) {
    flex-direction: column;
  }
}
hr {
  margin: .25rem 0;
}
article {
  flex: 0 0 auto;
}
.columns {
  overflow: auto;
  width: 100%;
}
.info-content-container {
  flex: 1;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  margin: .5rem 1rem;
}
.part-collection-info-content-container {
  margin-left: 0 !important;
  margin-right: 0 !important;
  justify-content: space-between;
}
.figure {
  margin: 0.5rem;
}
.info-thumbnail {
  width: 256px;
  height: 256px;
}
@media only screen and (max-width: 1120px) {
  .info-thumbnail {
    width: 200px;
    height: 200px;
  }
  .figure, .info-content-container {
    margin: 0;
  }
}

@media screen and (max-width: 768px) {
  .info-content-container {
    align-items: flex-start;
  }
}

.copy-part-number-btn {
  padding: 0;
  border: 0;
}

.part-number-row {
  width: fit-content;
  gap: 0.35rem;
  flex-wrap: wrap;
  @media screen and (max-width: 768px) {
    min-width: 15rem;
    overflow-y: hidden;
  }
}

.copy-part-number-icon {
  font-size: 0.7rem;
}
</style>
